import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import { useToast } from "@/hooks/use-toast";
import { esperaDoPedido, podeEnviarAoTecnico } from "@shared/laudo-envio-tv";
import type { LaudoWithDetails } from "@shared/schema";
import { posicoesFaltantes } from "@shared/veiculo-fotos";

// Mesmos textos do APP.
export const TEXTO_ENVIAR_ANALISE = "Enviar para análise";
export const TEXTO_ENVIAR_TECNICO = "Enviar para o Técnico de Vistoria";
export const AVISO_FOTOS_PARA_TECNICO = "O laudo vai ao Técnico de Vistoria quando as 4 fotos do veículo estiverem completas.";
export const AVISO_ATRIBUICAO_PELA_VISTORIADORA = "Pedido de envio registrado. A vistoriadora atribuirá o Técnico de Vistoria.";

export type ResultadoEnvioTecnico = { ok: true; tecnicoAtribuido: boolean } | { ok: false; message: string };

/** PUT /api/laudos/:id { enviarParaTecnico: true }. Nunca lança: o laudo já existe e a falha vira mensagem para o usuário. */
export async function solicitarEnvioTecnico(laudoId: string | undefined, token: string | undefined): Promise<ResultadoEnvioTecnico> {
  if (!laudoId) return { ok: false, message: "Não foi possível identificar o laudo criado." };
  try {
    const r = await fetch(`/api/laudos/${laudoId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ enviarParaTecnico: true }),
    });
    const corpo = await r.json().catch(() => null); // corpo vazio ou "null" não pode derrubar o tratamento (era TypeError e virava "verifique a conexão")
    if (!r.ok) return { ok: false, message: corpo?.message || "Não foi possível enviar o laudo ao Técnico de Vistoria. Tente novamente." };
    return { ok: true, tecnicoAtribuido: corpo?.tecnicoAtribuido === true };
  } catch {
    return { ok: false, message: "Não foi possível enviar o laudo ao Técnico de Vistoria. Verifique a conexão e tente novamente." };
  }
}

/** Cartão do detalhe do laudo para o Parceiro dono: botão do envio direto ao técnico ou o aviso do que o pedido espera. */
export function EnvioTecnicoVistoria({ laudo, onEnviado }: { laudo: LaudoWithDetails; onEnviado: () => void }) {
  const { profile, session } = useAuth();
  const { modulos } = useModulos();
  const { toast } = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const perfil = { empresaId: profile?.empresa?.id, roles: profile?.roles };
  const fotos = laudo.veiculo?.fotos ?? [];
  const faltamFotos = laudo.moduloDetranVeiculoFoto && posicoesFaltantes(fotos).length > 0;
  const pode = podeEnviarAoTecnico(perfil, laudo, modulos.tvUnico);
  const espera = perfil.empresaId === laudo.parceiroId ? esperaDoPedido(laudo, fotos) : null;
  if (!pode && !espera) return null;

  const enviar = async () => {
    setConfirmando(false);
    setEnviando(true);
    const r = await solicitarEnvioTecnico(laudo.id, session?.access_token);
    setEnviando(false);
    if (!r.ok) return toast({ title: "Não foi possível enviar ao Técnico de Vistoria", description: r.message, variant: "destructive" });
    toast({
      title: r.tecnicoAtribuido ? "Laudo enviado ao Técnico de Vistoria" : "Pedido de envio registrado",
      description: r.tecnicoAtribuido ? undefined : faltamFotos ? AVISO_FOTOS_PARA_TECNICO : AVISO_ATRIBUICAO_PELA_VISTORIADORA,
    });
    onEnviado();
  };

  if (!pode) {
    return (
      <p className="mb-6 flex items-start gap-2 rounded-lg border bg-slate-50 p-3 text-sm text-slate-600">
        <Send className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        {espera === "fotos" ? AVISO_FOTOS_PARA_TECNICO : AVISO_ATRIBUICAO_PELA_VISTORIADORA}
      </p>
    );
  }
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-lg border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 text-sm text-slate-600">
        <p className="font-semibold text-slate-900">Envio ao Técnico de Vistoria</p>
        <p>{faltamFotos ? AVISO_FOTOS_PARA_TECNICO : "Envie o laudo direto ao técnico, sem esperar a escolha da vistoriadora."}</p>
      </div>
      <Button onClick={() => setConfirmando(true)} disabled={enviando} className="shrink-0">
        {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {TEXTO_ENVIAR_TECNICO}
      </Button>
      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{TEXTO_ENVIAR_TECNICO}?</AlertDialogTitle>
            <AlertDialogDescription>
              Com o técnico atribuído, o laudo segue para a vistoria e você não poderá mais alterar o checklist.
              {faltamFotos && ` ${AVISO_FOTOS_PARA_TECNICO}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={enviar}>Enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
